const en = {
    nav: {
        brand: 'donata oppermann',
        about: 'about',
        contact: 'contact',
        home: 'home',
        categories: 'categories',
        switchTo: 'Switch language to German',
    },
    home: {
        sold: 'Sold',
        categories: {
            objekte: 'Objects',
            zeichnungen: 'Drawings',
            schalen: 'Bowls',
            schmuck: 'Jewellery',
            shop: 'Shop',
            urnen: 'Urns',
        },
        shop: {
            filters: 'Filters',
            allCategories: 'All',
            price: {
                label: 'Price',
                all: 'All prices',
                under100: 'under 100 €',
                '100to300': '100 – 300 €',
                over300: 'over 300 €',
            },
            sort: {
                label: 'Sort',
                newest: 'Newest',
                featured: 'Featured',
                priceAsc: 'Price low – high',
                priceDesc: 'Price high – low',
                nameAsc: 'Name A – Z',
            },
            results: (n) => (n === 1 ? '1 work' : `${n} works`),
            clear: 'Clear filters',
            empty: 'No works match your selection.',
            sold: 'sold',
            typeLabels: {
                Objekte: 'Objects',
                Zeichnungen: 'Drawings',
                Schmuck: 'Jewellery',
                Schalen: 'Bowls',
            },
        },
    },
    about: {
        eyebrow: 'about me',
        name: { first: 'donata', last: 'oppermann' },
        role: 'artist of three-dimensional objects and spatial installations',
        cue: 'scroll',
        manifesto: {
            partA: 'my objects are drawings in space.',
            partB: 'the thread is the line.',
            attribution: 'donata oppermann',
        },
        sections: [
            {
                title: 'my creative path',
                text: 'Working with my hands has always been important to me. I love exploring traditional handcraft techniques and discovering new directions within them. Working with my hands is, for me, a way of grasping and understanding the world. It is astonishing what my hands, my soul, my mind, and my senses can bring forth in this process. This freedom in handcraft often leads to surprising and joyful results — sometimes I am genuinely surprised by what emerges.',
                caption: 'studio practice — light & shadow',
                alt: 'artwork with light and shadow',
            },
            {
                title: 'spatial objects',
                text: 'My larger works are spatial objects made from paper yarn that I assemble on a sewing machine. Inspired by nature, I transform the material into large, mysterious-looking beings. These objects are like drawings in space, with the thread serving as the line. The line plays a central role in my work — it stands for beauty, movement, departure, breaking off, pausing, and expanding. I draw it from the diversity of nature, sometimes consciously and sometimes unconsciously, and bring it back into the object.',
                caption: 'paper yarn — the thread as line',
                alt: 'donata in her creative process',
            },
            {
                title: 'wall objects and installations',
                text: 'In another series I work with small, interrelated wall objects. Here I combine items such as porcelain doll legs or roe deer antlers with indeterminate organic forms made of crocheted paper. The result is surreal-looking pieces that, despite their humorous appearance, can recall nightmarish scenes. One of my spatial works draws a connection between magical European prehistory and present-day environmental concerns, placing the tall golden hats of prehistoric sun cults in dialogue with hats made of plastic waste.',
                caption: 'wall objects — found pieces & crocheted paper',
                alt: 'wall objects and installations',
            },
            {
                title: 'paper space-objects',
                text: 'In another series I work with cubic spatial fragments made from handmade, fragile paper. These objects respond both to one another and to the surrounding space. They evoke feelings of openness and shelter, but also of emptiness and exposure. These works speak to architecture as a sheltering space and as an extended human body.',
                caption: null,
                alt: null,
            },
        ],
        outro: 'see more works',
    },
    contact: {
        eyebrow: 'studio',
        title: 'contact',
        description:
            'Get in touch with Donata Oppermann to discuss commissions, available works, prices or exhibition opportunities.',
        channelsTitle: 'direct',
        email: 'Email',
        instagram: 'Instagram',
        location: 'Studio',
        fields: {
            name: 'Name',
            email: 'Email',
            message: 'Message',
            placeholder: {
                name: 'Your name',
                email: 'your@email.com',
                message: 'How can I help you?',
            },
        },
        send: 'Send message',
        thankYou: 'thank you',
        messageSent: 'Your message has been received. I will get back to you as soon as possible.',
        backToHome: 'back to overview',
        error: 'There was an error submitting your message. Please try again later.',
        addressLabel: 'Forststrasse 7, 92369 Sengenthal, Germany',
    },
    product: {
        loading: 'Loading…',
        notFound: 'Work not found',
        loadError: 'The work could not be loaded',
        contactForPrice: 'Price on request',
        sale: 'Sale',
        promo: {
            title: 'Promo code',
            placeholder: 'Enter code',
            apply: 'Apply',
            applying: '…',
            invalid: 'Code invalid or not applicable',
            error: 'Something went wrong. Please try again.',
            discount: 'Discount',
            remove: 'Remove',
            automatic: 'Promotion',
        },
        measurements: 'Dimensions',
        details: 'Details',
        specs: {
            material: 'Material',
            dimensions: 'Dimensions',
            weight: 'Weight',
            origin: 'Origin',
            category: 'Category',
            collection: 'Series',
            technique: 'Technique',
            year: 'Year',
            series: 'Series',
            benefits: 'Notable',
            edition: 'Edition',
            signature: 'Signature',
            condition: 'Condition',
            framed: 'Framed',
            exhibitions: 'Exhibitions',
        },
        related: {
            eyebrow: 'more',
            title: 'works in this series',
            empty: 'no other works',
        },
        breadcrumb: {
            back: 'back to overview',
        },
        gallery: {
            zoom: 'click to zoom',
            counter: (i, n) => `${String(i).padStart(2, '0')} / ${String(n).padStart(2, '0')}`,
            thumbAlt: (title, i) => `${title} – view ${i}`,
            close: 'Close',
            previous: 'Previous image',
            next: 'Next image',
        },
        stock: {
            inStock: 'in stock',
            fewLeft: (n) => `only ${n} left`,
            soldOut: 'sold out',
        },
        cta: {
            buy: 'purchase',
            request: 'request price',
        },
        priceRequest: {
            title: 'price request',
            description: 'Drop me a line — I will get back to you personally.',
            name: 'Name',
            email: 'Email',
            message: 'Message (optional)',
            messagePlaceholder: 'Your message …',
            submit: 'send enquiry',
            submitting: 'sending …',
            success: 'Thank you for your enquiry. I will get back to you as soon as possible.',
            errors: {
                nameRequired: 'Name is required',
                emailRequired: 'A valid email is required',
            },
        },
        contactOverlay: {
            title: 'Hello!',
            subtitle: 'Happy to answer any question about this work',
            email: 'Send an email',
            contactPage: 'Visit contact page',
            mailSubject: (title) => `Enquiry: ${title}`,
            mailBody: (title) =>
                `Hello! I'm interested in the work "${title}" and have the following question:`,
            buttonAria: 'Open contact overlay',
        },
        address: {
            title: 'shipping address',
            name: 'Name',
            namePlaceholder: 'Full name',
            nameRequired: 'Name is required',
            email: 'Email',
            emailPlaceholder: 'your@email.com',
            emailRequired: 'Email is required',
            emailInvalid: 'Invalid email address',
            line1: 'Street and house number',
            line1Placeholder: 'Main Street 123',
            line1Required: 'Address is required',
            postal: 'Postal code',
            postalPlaceholder: '12345',
            postalRequired: 'Postal code is required',
            city: 'City',
            cityPlaceholder: 'Berlin',
            cityRequired: 'City is required',
            country: 'Country',
            countryPlaceholder: 'Select country',
            countrySearch: 'Search country …',
            countryEmpty: 'No country found',
            countryRequired: 'Country is required',
            continue: 'continue to shipping',
            processing: 'processing …',
        },
        shipping: {
            title: 'shipping method',
            subtitle: 'Choose your preferred shipping method. Costs depend on the destination country.',
            continue: 'continue to payment',
            processing: 'processing …',
            back: 'back to address',
            free: 'Free',
            none: 'No shipping is currently available for this country. Please contact us.',
        },
        payment: {
            title: 'payment',
            orderFor: 'Order for',
            email: 'Email',
            shippingTo: 'Shipping to',
            summary: {
                item: 'Item',
                shipping: 'Shipping',
                discount: 'Discount',
                total: 'Total',
                free: 'Free',
            },
            pay: (amount) => `pay ${amount}`,
            processing: 'processing …',
            confirmation: 'You will receive a confirmation email after successful payment.',
            preparing: 'Preparing payment …',
            initFailed: 'Payment system could not be initialised. Please try again later.',
            errorTitle: 'Payment error',
            stripeNotReady: 'Stripe is not yet ready. Please wait a moment.',
            errors: {
                cardError: 'There was a problem with your card.',
                emptyParam: 'Please fill in all required fields.',
                invalidRequest: 'Something went wrong. Please check your input.',
                apiError: 'Server error. Please try again later.',
                generic: 'An unexpected error occurred.',
            },
        },
        purchase: {
            success: 'Thank you for your purchase! We will process your order shortly.',
            errorFallback: 'There was a problem with your order. Please try again.',
            noShipping: 'No shipping option available.',
            noStripe: 'Stripe session could not be created.',
            noVariant: 'Product has no variant.',
            freeOrderTitle: 'Complete order',
            freeOrderNote: 'The promotion brings your total to €0. No payment is required.',
            freeOrderCta: 'Place free order',
        },
    },
    faq: {
        eyebrow: 'Help & Shipping',
        title: 'Frequently Asked Questions',
        subtitle: 'Everything about shipping, packaging and ordering.',
        questionsHeading: 'More questions',
        shipping: {
            heading: 'Shipping costs',
            intro: 'Shipping costs depend on the size and type of the piece and are shown at checkout once you enter your delivery address. The following flat rates apply:',
            columns: { category: 'Category', de: 'Germany', eu: 'EU', world: 'Worldwide' },
            rows: [
                { category: 'Jewellery & small items', de: '€4.99', eu: '€6.99', world: '€13.90' },
                { category: 'Objects, drawings & bowls', de: '€6.99', eu: '€16.90', world: '€39.90' },
                { category: 'Large & bulky works', de: '€14.90', eu: '€29.90', world: '€59.90' },
            ],
            note: 'Every work is one of a kind. For especially large or framed pieces I am happy to arrange shipping with you individually.',
        },
        items: [
            {
                question: 'How long does shipping take?',
                answer: 'Each work is handmade and packed individually. After payment I usually ship within 3–5 working days. Delivery then takes 1–3 working days within Germany, 3–7 working days within the EU and correspondingly longer worldwide.',
            },
            {
                question: 'How are the works packaged?',
                answer: 'All works are packed carefully and securely. Delicate and framed pieces receive additional protection so they reach you intact.',
            },
            {
                question: 'Do you ship internationally?',
                answer: 'Yes. I ship within Germany, across the EU and worldwide. You can find the respective shipping costs in the table above and at checkout.',
            },
            {
                question: 'A work is marked “price on request” – how can I buy it?',
                answer: 'Some works are not available directly through the shop. For these pieces simply use the request form on the respective product page – I will get back to you personally.',
            },
            {
                question: 'How can I pay?',
                answer: 'Payment is handled securely via Stripe, including all common credit and debit cards.',
            },
        ],
    },
};

export default en;
